import * as Sentry from '@sentry/vue';
import { HttpNetworkError, type PageProps } from '@inertiajs/core';
import type { App } from 'vue';

export function initSentry(app: App, pageProps: PageProps): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN_PUBLIC;

  if (!dsn) {
    return;
  }

  Sentry.init({
    app,
    dsn,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    environment: pageProps.app?.env ?? import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),
    // Browser-extension artifacts, not application errors - see
    // https://github.com/getsentry/sentry-javascript/issues/3440
    ignoreErrors: [
      // Grammarly, LastPass, and other extensions injecting content into
      // iframes throw this legacy IE/Edge COM error when the page's DOM
      // mutates underneath them.
      'Object Not Found Matching Id',
      'Java object is gone',
      'window.webkit.messageHandlers',
    ],
    // Facebook's in-app browser (Android) injects its own scripts under the
    // iabjs:// scheme - e.g. a navigation/perf logger that throws
    // "Java object is gone" after the IAB closes, or a malformed
    // "browser_declutter" script that fails to parse. These errors
    // originate entirely within Facebook's injected code, not ours.
    denyUrls: [/^iabjs:\/\//],
  });

  const user = pageProps.auth?.user;

  if (!user) {
    return;
  }

  Sentry.setUser({
    id: user.id ?? undefined,
    email: user.email,
    username: user.name ?? undefined,
  });

  if (user.company) {
    Sentry.setTag('user.company_name', user.company.name);
    Sentry.setTag('user.company_id', user.company.id);
  }
}

// Inertia v3's networkError event fires for any uncaught error during a visit, not
// just network failures - keep genuine errors at error level so they still alert.
export function captureInertiaVisitError(error: unknown): void {
  Sentry.captureException(error, {
    level: error instanceof HttpNetworkError ? 'warning' : 'error',
    tags: { source: 'inertia-network-error' },
  });
}
